import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Phone, Calendar, Clock, User, MapPin, CheckCircle, ThermometerSun, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { companyInfo } from '../../data/mock';

const AirConditionerServiceRoseville = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#FDFCF7]" data-testid="blog-post-ac-service-2">
      {/* Navigation Bar */}
      <div className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/blog')}
            data-testid="back-to-blog-btn"
            className="flex items-center gap-2 text-[#622B62] hover:text-[#DA9C3A] transition-colors font-medium"
          >
            <ArrowLeft size={20} />
            Back to Blog
          </button>
          <a href={`tel:${companyInfo.phone}`}>
            <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-semibold px-5 py-2 rounded-full">
              <Phone size={16} className="mr-2" />
              {companyInfo.phone}
            </Button>
          </a>
        </div>
      </div>

      {/* Article Hero */}
      <div className="bg-[#622B62] text-white py-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#7A3D7A] rounded-full -translate-y-1/2 translate-x-1/2 opacity-50" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#4A1F4A] rounded-full translate-y-1/2 -translate-x-1/2 opacity-50" />
        <div className="max-w-3xl mx-auto px-4 relative z-10">
          <span className="inline-block bg-[#DA9C3A] text-white text-xs font-bold px-3 py-1 rounded-full mb-4">Air Conditioner Service</span>
          <h1 className="romano-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight" data-testid="article-heading" data-speakable="true">
            Air Conditioner Service in Roseville, CA & Surrounding Cities — What Every Homeowner Should Know
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
            <span className="flex items-center gap-1"><User size={14} /> Juliano Romano</span>
            <span className="flex items-center gap-1"><Calendar size={14} /> June 2, 2026</span>
            <span className="flex items-center gap-1"><Clock size={14} /> 10 min read</span>
            <span className="flex items-center gap-1"><MapPin size={14} /> Roseville, CA</span>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-3xl mx-auto px-4 py-12">
        <div className="prose prose-lg max-w-none">

          {/* Personal Intro */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 mb-10 shadow-sm" data-speakable="true">
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Hey there — I'm Juliano Romano, and I've been fixing air conditioners in Roseville and the surrounding Sacramento area for over 20 years now. If there's one thing I've learned in two decades of crawling around attics and tinkering with compressors, it's this: <strong>a little preventive care goes a long way</strong>.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-0">
              I wrote this guide because I get the same questions from homeowners all over Roseville, Rocklin, Folsom, and beyond. Whether your AC is making a weird noise or you're just wondering if it's time for a tune-up, this one's for you. Let's talk <strong>air conditioner service in Roseville, CA</strong> — no jargon, no sales pitch, just honest advice from someone who does this every single day.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-[#622B62]/5 rounded-2xl p-6 mb-10">
            <h2 className="romano-heading text-xl font-bold text-[#622B62] mb-4">What We'll Cover</h2>
            <ul className="space-y-2">
              {[
                'Why Roseville Summers Are So Hard on Air Conditioners',
                'The Honest Truth About AC Maintenance',
                'What We Actually Do During a Service Call',
                '"Is My AC Dying?" — Warning Signs to Watch For',
                'How to Pick an HVAC Company You Can Trust',
                'Quick Tips to Keep Your AC Happy Between Visits',
                'We Come to You — All the Cities We Serve',
                'A Personal Note from Juliano',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-[#DA9C3A] font-bold">{i + 1}.</span> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Section 1 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Why Roseville Summers Are So Hard on Air Conditioners
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Look, I love living in Roseville. The people are great, the community is strong, and there's nowhere else I'd rather run my business. But our summers? They're <em>brutal</em> on air conditioners.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            We're talking 100°F to 110°F from June through September — sometimes starting as early as May. Your air conditioner isn't just running during those months; it's running <strong>hard</strong>, often 10 to 14 hours a day. That's like asking your car to drive non-stop from Sacramento to New York and back, every single week, all summer long.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            And it's not just Roseville. Neighbors in Rocklin, Granite Bay, and Lincoln deal with the same heat. Folsom and El Dorado Hills sometimes get it even worse because of the elevation. The whole Sacramento Valley turns into an oven, and your AC is the only thing standing between you and a very miserable evening.
          </p>
          <div className="bg-[#DA9C3A]/10 border-l-4 border-[#DA9C3A] p-5 rounded-r-xl my-6">
            <p className="text-gray-800 font-medium mb-0">
              Here's a number that might surprise you: an unmaintained air conditioner loses about 5% of its efficiency every year. After just 3 or 4 years without service, you could be paying 15–20% more on your energy bills — and your system is working overtime just to keep up.
            </p>
          </div>

          {/* Section 2 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            The Honest Truth About AC Maintenance
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            I'll be straight with you — I'm not the kind of guy who's going to tell you your system needs a $5,000 replacement when a $200 repair will do the job. That's not how we operate at Romano Heating and Cooling, and it never has been.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            But I <em>will</em> tell you that skipping regular air conditioner service is like never changing the oil in your car. Sure, it'll run fine for a while. But one day you're going to be stuck on the side of the road — or in our case, stuck in a 108°F house with no cool air, calling every HVAC company in Roseville and hoping someone can come out today.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            The best time to get your air conditioner serviced? <strong>Spring</strong>. March, April, early May — before the rush hits. Every year around mid-June, our phones start ringing off the hook with emergency calls. Most of those breakdowns could've been prevented with a simple tune-up a few weeks earlier.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            That's not me trying to sell you something. That's just the reality after 20+ years in this business.
          </p>

          {/* Section 3 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            What We Actually Do During an Air Conditioner Service Call
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            When one of our technicians comes out for an air conditioner service visit in Roseville (or any of the surrounding cities we serve), here's what actually happens — no mystery, no surprises:
          </p>
          <div className="space-y-3 my-6">
            {[
              { step: 'We inspect the whole system', detail: 'Not just a quick glance — we check every component from the thermostat to the condenser unit outside.' },
              { step: 'Clean the coils', detail: 'Dirty evaporator and condenser coils are the #1 reason ACs lose efficiency. We clean them thoroughly.' },
              { step: 'Check your refrigerant', detail: 'Low refrigerant means your AC is working harder and cooling less. We check levels and top off if needed.' },
              { step: 'Test electrical connections', detail: 'Loose or corroded connections can cause all sorts of problems — from tripped breakers to compressor failure.' },
              { step: 'Lubricate moving parts', detail: 'Friction kills motors. A little lubrication goes a long way toward extending the life of your system.' },
              { step: 'Check the condensate drain', detail: 'A clogged drain can cause water damage in your home. We make sure it\'s clear and flowing.' },
              { step: 'Replace the air filter', detail: 'A clean filter means better airflow, better air quality, and less strain on your system.' },
              { step: 'Calibrate your thermostat', detail: 'If your thermostat is off by even a couple degrees, your system isn\'t running as efficiently as it should.' },
              { step: 'Run a full performance test', detail: 'We measure temperatures, airflow, and system pressures to make sure everything is operating the way it should.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                <div className="w-8 h-8 rounded-full bg-[#622B62] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-sm font-bold">{i + 1}</span>
                </div>
                <div>
                  <span className="font-bold text-[#622B62]">{item.step}</span>
                  <span className="text-gray-600"> — {item.detail}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            The whole visit usually takes about an hour, and when we're done, you'll know exactly how your system is doing. No upselling, no pressure — just a clear picture of where things stand.
          </p>

          {/* Section 4 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            "Is My AC Dying?" — Warning Signs to Watch For
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            I hear this question all the time from homeowners in Roseville, Rocklin, and Folsom. Here's how I usually answer: your AC will almost always give you warning signs before it gives up completely. The trick is knowing what to look for.
          </p>
          <div className="space-y-4 my-6">
            {[
              { sign: 'Your energy bills are creeping up', detail: 'If your PG&E bill keeps climbing but your habits haven\'t changed, your AC is probably losing efficiency. This is often the first sign.' },
              { sign: 'It\'s making sounds it didn\'t used to make', detail: 'Grinding, rattling, buzzing, or squealing — none of these are normal. Each one points to a different issue, and most are fixable if caught early.' },
              { sign: 'Some rooms are hot, others are cold', detail: 'Uneven cooling usually means airflow problems — could be dirty ducts, a failing blower motor, or issues with your ductwork.' },
              { sign: 'It\'s blowing warm air', detail: 'This is the obvious one. Could be low refrigerant, a bad compressor, or something as simple as a tripped breaker. Either way, call us.' },
              { sign: 'It\'s cycling on and off constantly', detail: 'Short cycling puts massive stress on your compressor. Left unchecked, it can turn a $300 repair into a $3,000 replacement.' },
              { sign: 'You smell something off', detail: 'Musty smell? Probably mold in your ducts. Burning smell? Could be an electrical issue. Neither should be ignored.' },
              { sign: 'It\'s over 15 years old', detail: 'AC units typically last 15–20 years with good maintenance. If yours is getting up there, it\'s worth having us take a look and give you an honest assessment.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle size={18} className="text-[#DA9C3A] mt-1 flex-shrink-0" />
                  <div>
                    <span className="font-bold text-[#622B62]">{item.sign}</span>
                    <p className="text-gray-600 mt-1 mb-0">{item.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            If any of these sound familiar, don't panic — most of the time it's something we can fix quickly and affordably. The key is not waiting until a 105°F day in July to find out.
          </p>

          {/* Section 5 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            How to Pick an HVAC Company You Can Trust in Roseville
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            I know I'm biased here, but I'll share what I'd look for if I were hiring someone to work on <em>my</em> home's air conditioner:
          </p>
          <ul className="space-y-3 my-6">
            {[
              { point: 'They\'re licensed and insured', detail: 'In California, that means a valid C-20 HVAC contractor license. Ours is #1077499 — you can look it up on the CSLB website.' },
              { point: 'They\'ve been around a while', detail: 'Fly-by-night companies pop up every summer. We\'ve been serving Roseville since 2005.' },
              { point: 'They give you a price before they start', detail: 'If someone won\'t tell you what it\'ll cost before they start working, that\'s a red flag. We always provide upfront estimates.' },
              { point: 'They actually explain what they\'re doing', detail: 'You shouldn\'t need an engineering degree to understand what\'s wrong with your AC. Our guys walk you through everything.' },
              { point: 'They have real reviews from real neighbors', detail: 'Check Google, check Yelp. Talk to your neighbors. Reputation matters — especially in a community like Roseville.' },
              { point: 'They\'re available when you need them', detail: 'Emergencies don\'t wait for business hours. We offer 24/7 emergency service because your comfort shouldn\'t have to wait.' },
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle size={20} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="font-bold text-[#622B62]">{item.point}</span>
                  <span className="text-gray-600"> — {item.detail}</span>
                </div>
              </li>
            ))}
          </ul>

          {/* Section 6 */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            Quick Tips to Keep Your AC Happy Between Service Visits
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            You don't need to be an HVAC tech to take good care of your air conditioner. Here are some simple things any Roseville homeowner can do:
          </p>
          <div className="grid gap-4 my-6">
            {[
              { tip: 'Change your filter every month during summer', desc: 'I know, I know — it seems like a lot. But during peak season in Roseville, your filter is working overtime. A $5 filter can save you a $500 repair.' },
              { tip: 'Keep the area around your outdoor unit clear', desc: 'Give it at least 2 feet of clearance on all sides. Trim back bushes, clear away debris. Your condenser needs to breathe.' },
              { tip: 'Don\'t close too many vents', desc: 'I see this a lot — homeowners close vents in unused rooms thinking it\'ll save energy. It actually creates pressure imbalances that make your system work harder.' },
              { tip: 'Use a programmable thermostat', desc: 'Set it to 78°F when you\'re home and 85°F when you\'re away. If you don\'t have a smart thermostat yet, we install them — and they usually pay for themselves within a year.' },
              { tip: 'Keep your curtains closed during the hottest part of the day', desc: 'Especially on south and west-facing windows. It sounds simple, but it can reduce the load on your AC by 10–15%.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h4 className="font-bold text-[#622B62] mb-1">{item.tip}</h4>
                <p className="text-gray-600 text-sm mb-0">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Section 7 - GEO */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            We Come to You — All the Cities We Serve for Air Conditioner Service
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our shop is right here on Church Street in Roseville, but we're on the road all day servicing air conditioners across the region. If you live in any of these communities, we're just a phone call away:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 my-6">
            {[
              { city: 'Roseville, CA', note: 'Our home base since 2005' },
              { city: 'Rocklin, CA', note: 'Right next door — fast response times' },
              { city: 'Granite Bay, CA', note: 'We service many homes in the area' },
              { city: 'Lincoln, CA', note: 'Growing fast — we grow with you' },
              { city: 'Loomis, CA', note: 'Small town feel, big-time service' },
              { city: 'Folsom, CA', note: 'A quick drive down I-80' },
              { city: 'El Dorado Hills, CA', note: 'Hill country gets hot too' },
              { city: 'Citrus Heights, CA', note: 'Serving you for years' },
              { city: 'Orangevale, CA', note: 'Just down the road' },
              { city: 'Fair Oaks, CA', note: 'A community we love working in' },
              { city: 'Carmichael, CA', note: 'Dependable service, always' },
              { city: 'Sacramento, CA', note: 'Covering the greater Sac area' },
              { city: 'Antelope, CA', note: 'North Sac — we\'ve got you' },
              { city: 'Auburn, CA', note: 'Foothills, no problem' },
              { city: 'Penryn, CA', note: 'A short drive from our shop' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <MapPin size={16} className="text-[#DA9C3A] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-gray-800 font-medium text-sm">{item.city}</span>
                  <p className="text-gray-500 text-xs mb-0">{item.note}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-gray-700 leading-relaxed mb-4">
            Don't see your city? Give us a call anyway — chances are we can help. We've been known to stretch our service area for folks who ask nicely.
          </p>

          {/* Section 8 - Personal Note */}
          <h2 className="romano-heading text-2xl font-bold text-[#622B62] mt-12 mb-4" data-speakable="true">
            A Personal Note from Juliano
          </h2>
          <div className="bg-[#622B62]/5 rounded-2xl p-8 my-6 border border-[#622B62]/10">
            <p className="text-gray-700 leading-relaxed mb-4">
              When I started Romano Heating and Cooling back in 2005, it was just me, a truck, and a determination to do things the right way. Twenty-plus years later, the truck is a little bigger (okay, a lot bigger), but the philosophy hasn't changed.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              I treat every home like it's my own. I tell my team the same thing I tell myself: if you wouldn't recommend this repair to your own mom, don't recommend it to a customer.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Roseville is my home. Rocklin, Folsom, Granite Bay — these are places where my kids go to school, where I grab coffee in the morning, where I run into customers at the grocery store. That matters. When your name is on the side of the truck, you take this stuff personally.
            </p>
            <p className="text-gray-700 leading-relaxed mb-0">
              So whether your air conditioner needs a tune-up, a repair, or just a second opinion — give us a call. I'd love to help.
            </p>
            <div className="mt-6 pt-6 border-t border-[#622B62]/10">
              <p className="font-bold text-[#622B62] mb-0">— Juliano Romano</p>
              <p className="text-gray-500 text-sm mb-0">Owner, Romano Heating and Cooling · LIC #1077499</p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#622B62] to-[#4A1F4A] rounded-3xl p-10 text-center text-white relative overflow-hidden my-12">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <h3 className="romano-heading text-2xl sm:text-3xl font-bold mb-4">
                Ready to Get Your AC Checked Out?
              </h3>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Whether you're in Roseville, Rocklin, Folsom, or anywhere nearby — we'll take good care of you. Free estimates, upfront pricing, and no surprises. That's the Romano way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:9162749021">
                  <Button className="bg-[#DA9C3A] hover:bg-[#C48A2A] text-white font-bold px-10 py-6 text-lg rounded-full shadow-xl">
                    <Phone size={22} className="mr-2" />
                    Call (916) 274-9021
                  </Button>
                </a>
                <Button
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#622B62] font-bold px-10 py-6 text-lg rounded-full"
                  onClick={() => navigate('/#contact')}
                >
                  Get a Free Estimate
                </Button>
              </div>
            </div>
          </div>

          {/* Related Links */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 my-8">
            <h4 className="font-bold text-[#622B62] mb-3">More from Romano Heating & Cooling</h4>
            <ul className="space-y-2">
              <li><Link to="/maintenance-plans" className="text-[#DA9C3A] font-medium hover:underline">View Our Maintenance Plans (starting at $9.99/mo)</Link></li>
              <li><Link to="/blog/air-conditioning-service-roseville-ca" className="text-[#DA9C3A] font-medium hover:underline">Air Conditioning Service Roseville CA — Technical Guide</Link></li>
              <li><Link to="/services/all" className="text-[#DA9C3A] font-medium hover:underline">All Heating & Cooling Services</Link></li>
              <li><Link to="/faq" className="text-[#DA9C3A] font-medium hover:underline">Frequently Asked Questions</Link></li>
            </ul>
          </div>

          {/* Author Box */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 flex items-start gap-5 mt-8">
            <img
              src={companyInfo.logo}
              alt="Romano Heating & Cooling"
              className="w-16 h-16 rounded-lg object-contain flex-shrink-0"
            />
            <div>
              <h4 className="font-bold text-[#622B62]">Juliano Romano</h4>
              <p className="text-sm text-gray-500 mb-2">Owner, Romano Heating & Cooling</p>
              <p className="text-gray-600 text-sm">
                Juliano has been providing honest, dependable air conditioner service to Roseville, CA and surrounding communities since 2005. When he's not working on HVAC systems, he's spending time with his family right here in the Roseville area. Licensed contractor #1077499.
              </p>
            </div>
          </div>

        </div>
      </article>

      {/* Footer */}
      <div className="bg-[#622B62] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={companyInfo.logo} alt="Romano Heating & Cooling" className="h-24 w-24 object-contain rounded-lg" />
            <div className="text-left">
              <h3 className="font-bold romano-heading">Romano Heating & Cooling</h3>
              <p className="text-white/70 text-sm">1316 Church Street, Roseville, CA 95678</p>
            </div>
          </div>
          <p className="text-[#DA9C3A] font-semibold mb-2">(916) 274-9021</p>
          <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} Romano Heating and Cooling. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default AirConditionerServiceRoseville;
